import React, {useEffect, useState} from "react";
import {Autocomplete, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField} from "@mui/material";
import {AxiosResponse} from "axios";
import {
    ImpersonationCandidateDto,
    ImpersonationRestControllerApiFactory,
    ImpersonationStatusDto,
    UserAuthItemTypeEnum
} from "arteco-api-client-ts";
import {useApiManager} from "../../utils/api";
import {useAuthManager} from "../../utils/auth";
import {envVars} from "../../utils/env_vars";

/**
 * «Ver como» (#13810): solo existe si el backend lo admite (local y con ARTECO_DEV_IMPERSONATION=true).
 * El cambio de usuario lo hace el backend con un POST de formulario, que navega y vuelve a /app:
 * antes se olvida el usuario guardado para que el layout lo pida de nuevo, ya con la otra identidad.
 */
export const useImpersonation = () => {
    const authManager = useAuthManager();
    const apiManager = useApiManager();
    const [status, setStatus] = useState<ImpersonationStatusDto | undefined>();
    const logged = authManager.isLogged();

    useEffect(() => {
        if (!logged) {
            return;
        }
        const api = apiManager.factory(ImpersonationRestControllerApiFactory);
        apiManager.executeRaw(api.impersonationStatus(), undefined, true)
            .then((resp: AxiosResponse<ImpersonationStatusDto>) => setStatus(resp.data))
            .catch(() => setStatus(undefined));
    }, [logged]);

    const post = (path: string, username?: string) => {
        authManager.clear();
        const form = document.createElement("form");
        form.method = "POST";
        form.action = envVars.apiBasePath + path;
        if (username) {
            const input = document.createElement("input");
            input.type = "hidden";
            input.name = "username";
            input.value = username;
            form.appendChild(input);
        }
        document.body.appendChild(form);
        form.submit();
    };

    const isAdmin = authManager.getAuthorities().some(a => a.type === UserAuthItemTypeEnum.ADMIN);

    return {
        impersonator: status?.impersonator,
        canStart: !!status?.available && !status?.impersonator && isAdmin,
        start: (username: string) => post("/login/impersonate", username),
        exit: () => post("/logout/impersonate"),
    };
};

export const ImpersonationBanner = (props: { username?: string, impersonator: string, onExit: () => void }) => (
    <Box sx={{
        backgroundColor: "#b26a00", color: "white", px: 2, py: 0.5,
        display: "flex", alignItems: "center", justifyContent: "center", gap: 2, flexWrap: "wrap"
    }}>
        <span>Viendo como <b>{props.username}</b> (eres {props.impersonator})</span>
        <Button size="small" variant="outlined" color="inherit" onClick={props.onExit}>
            Volver a mi usuario
        </Button>
    </Box>
);

export const ImpersonationDialog = (props: { open: boolean, onClose: () => void, onStart: (username: string) => void }) => {
    const apiManager = useApiManager();
    const [candidates, setCandidates] = useState<ImpersonationCandidateDto[]>([]);
    const [selected, setSelected] = useState<ImpersonationCandidateDto | null>(null);

    useEffect(() => {
        if (!props.open) {
            return;
        }
        const api = apiManager.factory(ImpersonationRestControllerApiFactory);
        apiManager.execute(api.impersonationCandidates()).then(setCandidates);
    }, [props.open]);

    return (
        <Dialog open={props.open} onClose={props.onClose} fullWidth maxWidth="xs">
            <DialogTitle>Ver como…</DialogTitle>
            <DialogContent>
                <Autocomplete
                    sx={{mt: 1}}
                    options={candidates}
                    value={selected}
                    onChange={(_, value) => setSelected(value)}
                    getOptionLabel={c => `${c.fullName} (${c.username})`}
                    isOptionEqualToValue={(a, b) => a.username === b.username}
                    renderInput={params => <TextField {...params} label="Empleado" autoFocus/>}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={props.onClose}>Cancelar</Button>
                <Button variant="contained" disabled={!selected?.username}
                        onClick={() => selected?.username && props.onStart(selected.username)}>
                    Ver como
                </Button>
            </DialogActions>
        </Dialog>
    );
};
